// useStoreInfo.ts

import { computed, ref } from 'vue'
import stores, { type HoursType, type StoresDataType } from '@/storage/stores'
import { type OpeningHoursType } from '@/storage/setting'
import { storeIsOpen } from '@/event/storeIsOpen'
import { configDeliveryTime } from '@/storage/setting/configDeliveryTime'
import { getMinutesByData } from './time/getMinutesByData'
import { orderIsOpen } from '@/event/orderIsOpen'
import { getHourAndMinuteByTime } from './time/getTimeInHourAndMinute'
import { arrayPlanning } from '@/happyTheme/event/store/arrayPlanning'
import { getNextDayOpening } from './store/getNextDayOpening'

const { state } = stores
const { storeData } = state

export const title = ref()
export const classTitle = ref()
export const textTitle = ref('')
let intervalId: number | undefined


export function useStoreInfo() {
  const testDay = ref<number | null>(null)
  const testHour = ref<number | null>(null)
  const testMinute = ref<number | null>(null)
  const testTime = ref<number | null>(null)

  const updateStore = (storeData: StoresDataType) => {
    const classOpen = 'open-store'
    const classClose = 'close-store'
    const classDefault = 'default-siege'

    const textForOpen = `Le système de commande est actif, vous pouvez passer votre commande et la récupérer à partir de `
    const textOpenClose =
      'Le restaurant est fermé pour la commande en ligne. Veuillez réessayer plus tard.'
    const textTodayClose = "Le restaurant a fermé pour aujourd'hui. Veuillez revenir demain"
    const textClosed = "Le restaurant est actuellement fermé."
    const textClosedForTonight = 'Le restaurant est fermé pour ce soir. Veuillez revenir demain'
    const textOrderClosedStoreOpen = "Le restaurant est ouvert mais les commandes en ligne sont fermées pour le moment."

    // const textNever
    const textNotsortedPlanning = "Le restaurant n'a pas fourni d'horaire d'ouverture"

    if (storeData.name) {
      const storeConfigWebOrder = storeData.setting.find(
        (setting: { key: string }) => setting.key === 'config_web_order'
      )
      const configWebOrder = storeConfigWebOrder ?? storeData.config.web_order
      const configOpeningHours = storeData.setting.find(
        (setting: { key: string }) => setting.key === 'config_opening_hours'
      )

      const forwardOrdering = configDeliveryTime.value?.forward_ordering
      const instantOrdering = configDeliveryTime.value?.instant_ordering

      if (configOpeningHours) {
        arrayPlanning.value = (configOpeningHours.value as OpeningHoursType).pickup_service.length > 0 ?
          (configOpeningHours.value as OpeningHoursType).pickup_service : (configOpeningHours.value as OpeningHoursType).opening_hours

      } else {
        arrayPlanning.value = []
      }

      if (Number(configWebOrder.value) !== 0) {
        const today = new Date()
        const todayDay = testDay.value !== null ? testDay.value : today.getUTCDay() || 7
        const todayHours = testHour.value !== null ? testHour.value : today.getHours()
        const todayMinutes = testMinute.value !== null ? testMinute.value : today.getMinutes()
        const currentTime = getMinutesByData(`${todayHours}:${todayMinutes}`)

        let effectiveDay = todayDay

        let nextOpeningTime: number | string | null = ''

        const sortedPlanning: HoursType[] = [...arrayPlanning.value].sort((a, b) => {
          const [aHour] = a.open.split(':').map(Number)
          const [bHour] = b.open.split(':').map(Number)
          return aHour - bHour
        })

        const daysOfWeek = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
        const allDays = sortedPlanning.map((planning) => planning.days).flat()

        let nextDayOpening: HoursType | undefined | null = null
        let searchDay = { value: effectiveDay }

        nextDayOpening = getNextDayOpening(sortedPlanning, searchDay, nextDayOpening)

        if (!forwardOrdering && (!instantOrdering || instantOrdering === '0')) {
          storeIsOpen.value = false
          updateUI(textOpenClose, classClose, storeData.name)
          return
        }

        const nextOpeningDayName = daysOfWeek[searchDay.value === 7 ? 0 : searchDay.value]
        nextOpeningTime = nextDayOpening ? nextDayOpening.open : ''

        const currentDayPlanning = sortedPlanning.filter(sp =>
          sp.days.includes(effectiveDay.toString())
        )

        const lastPeriod = currentDayPlanning[currentDayPlanning.length - 1]
        const closeTimeDay = getMinutesByData(lastPeriod.close)

        if (!allDays.includes(effectiveDay.toString()) && sortedPlanning.length > 0) {
          const textNextOpen = `Il ouvrira ${nextOpeningDayName} à ${nextOpeningTime}`
          updateUI(
            `Le restaurant est fermé aujourd'hui. ${textNextOpen}`,
            classClose,
            storeData.name
          )
          return
        }

        const isInsideOpeningHours = currentDayPlanning.some(sp => {
          const openTime = getMinutesByData(sp.open)
          const closeTime = getMinutesByData(sp.close)
          return currentTime >= openTime && currentTime <= closeTime
        })

        const isOutsideOpeningHours = !isInsideOpeningHours

        if (currentDayPlanning && currentDayPlanning.length === 0) {
          orderIsOpen.value = false
          storeIsOpen.value = false
          updateUI(textTodayClose, classClose, storeData.name)
          return
        }

        const nextOpeningPeriod = currentDayPlanning.find(sp => currentTime < getMinutesByData(sp.open))

        if (instantOrdering === '1' && !forwardOrdering) {
          const textNextOpen = `Il ouvrira ${nextOpeningDayName} à ${nextOpeningTime}`

          if (isInsideOpeningHours) {
            orderIsOpen.value = true
            storeIsOpen.value = true
            updateUI('', classOpen, storeData.name)
            return
          } else if (isOutsideOpeningHours) {
            orderIsOpen.value = false
            storeIsOpen.value = false
            if (nextOpeningPeriod) {
              updateUI(
                textForOpen + `${nextOpeningPeriod.open}`,
                classDefault,
                storeData.name)
              return
            } else if (nextDayOpening) {
              updateUI(
                `Le restaurant est fermé aujourd'hui. ${textNextOpen}`,
                classClose,
                storeData.name
              )
              return
            }

            updateUI(textClosed, classClose, storeData.name)
            return
          }
        } else if ((instantOrdering === '0' || !instantOrdering) && forwardOrdering) {
          const textNextOpen = `Il ouvrira ${nextOpeningDayName} à ${nextOpeningTime}`

          if (forwardOrdering === 'never') {
            orderIsOpen.value = false
            storeIsOpen.value = false
            updateUI(textOpenClose, classClose, storeData.name)
            return
          } else if (forwardOrdering === 'before_closing') {
            nextOpeningTime = null

            if (nextOpeningPeriod) {
              nextOpeningTime = getMinutesByData(nextOpeningPeriod.open)
            } else {
              nextOpeningTime = null
            }

            if (isOutsideOpeningHours) {
              if (nextOpeningTime
                && currentTime < closeTimeDay
                && currentTime < nextOpeningTime) {
                storeIsOpen.value = true
                orderIsOpen.value = true
                updateUI(
                  textForOpen + `${getHourAndMinuteByTime(nextOpeningTime).join(':')}`,
                  classDefault,
                  storeData.name)
                return
              } else if (nextDayOpening) {
                orderIsOpen.value = false
                storeIsOpen.value = false
                updateUI(
                  `Le restaurant est fermé aujourd'hui. ${textNextOpen}`,
                  classClose,
                  storeData.name
                )
                return
              }
            } else {
              orderIsOpen.value = false
              storeIsOpen.value = true
              updateUI(textOrderClosedStoreOpen, classOpen, storeData.name)
              return
            }
          } else if (forwardOrdering === 'after_opening') {
            if (isInsideOpeningHours) {
              orderIsOpen.value = true
              storeIsOpen.value = true
              updateUI('', classOpen, storeData.name)
              return
            } else if (isOutsideOpeningHours) {
              orderIsOpen.value = false
              storeIsOpen.value = false

              if (nextOpeningPeriod) {
                updateUI(
                  textForOpen + `${nextOpeningPeriod.open}`,
                  classDefault,
                  storeData.name)
                return
              } else if (nextDayOpening) {
                updateUI(
                  `Le restaurant est fermé aujourd'hui. ${textNextOpen}`,
                  classClose,
                  storeData.name
                )
                return
              }
            }
          } else if (forwardOrdering === 'anytime') {
            if (isInsideOpeningHours) {
              orderIsOpen.value = true
              storeIsOpen.value = true
              updateUI('', classOpen, storeData.name)
            } else if (isOutsideOpeningHours) {
              if (nextOpeningPeriod) {
                orderIsOpen.value = true
                storeIsOpen.value = true
                updateUI(
                  textForOpen + `${nextOpeningPeriod.open}`,
                  classDefault,
                  storeData.name)
              } else if (nextDayOpening) {
                orderIsOpen.value = false
                storeIsOpen.value = false
                updateUI(
                  `Le restaurant est fermé aujourd'hui. ${textNextOpen}`,
                  classClose,
                  storeData.name
                )
                return
              } else {
                orderIsOpen.value = true
                storeIsOpen.value = true
                updateUI('', classOpen, storeData.name)
              }
            }
            return
          }
        } else if (instantOrdering === '1' && forwardOrdering) {
          const textNextOpen = `Il ouvrira ${nextOpeningDayName} à ${nextOpeningTime}`
          switch (forwardOrdering) {
            case 'never':
              if (isInsideOpeningHours) {
                orderIsOpen.value = true
                storeIsOpen.value = true
                updateUI('', classOpen, storeData.name)
                return
              } else if (isOutsideOpeningHours && currentTime < closeTimeDay && nextOpeningPeriod) {
                const textClosedNowButOpenLater = textClosed + ` Vous pouvez passer votre commande à partir de ${nextOpeningPeriod.open}.`

                orderIsOpen.value = false
                storeIsOpen.value = false
                updateUI(textClosedNowButOpenLater, classDefault, storeData.name)
                return
              }
              orderIsOpen.value = false
              storeIsOpen.value = false
              updateUI(textOpenClose, classClose, storeData.name)
              break

            case 'before_closing':
              nextOpeningTime = null

              if (nextOpeningPeriod) {
                nextOpeningTime = getMinutesByData(nextOpeningPeriod.open)
              } else {
                // Si aucune période à venir, le restaurant est déjà ouvert ou fermé pour la journée
                nextOpeningTime = null
              }

              if (isOutsideOpeningHours) {
                if (nextOpeningTime
                  && currentTime < closeTimeDay
                  && currentTime < nextOpeningTime) {
                  storeIsOpen.value = true
                  orderIsOpen.value = true
                  // le DP est fermer en dehors des horaires d'ouverture
                  updateUI(
                    textForOpen + `${getHourAndMinuteByTime(nextOpeningTime).join(':')}`,
                    classDefault,
                    storeData.name)
                  return
                } else if (nextDayOpening) {
                  orderIsOpen.value = false
                  storeIsOpen.value = false
                  updateUI(
                    `Le restaurant est fermé aujourd'hui. ${textNextOpen}`,
                    classClose,
                    storeData.name
                  )
                  return
                }
              } else {
                // SelectHours est fermer pendant les horaires d'ouverture mais pas le DP 
                orderIsOpen.value = true
                storeIsOpen.value = true
                updateUI('', classOpen, storeData.name)
                return
              }
              break
            case 'after_opening':
              if (isInsideOpeningHours) {
                orderIsOpen.value = true
                storeIsOpen.value = true
                updateUI('', classOpen, storeData.name)
                return
              } else if (isOutsideOpeningHours) {
                orderIsOpen.value = false
                storeIsOpen.value = false

                if (nextOpeningPeriod) {
                  const textClosedNowButOpenLater = textClosed + ` Vous pouvez passer votre commande et la récupérer à partir de ${nextOpeningPeriod.open}.`
                  updateUI(
                    textClosedNowButOpenLater,
                    classDefault,
                    storeData.name)
                  return
                } else if (nextDayOpening) {
                  updateUI(
                    `Le restaurant est fermé aujourd'hui. ${textNextOpen}`,
                    classClose,
                    storeData.name
                  )
                }
              }
              break
            case 'anytime':
              if (isInsideOpeningHours) {
                orderIsOpen.value = true
                storeIsOpen.value = true
                updateUI('', classOpen, storeData.name)
              } else if (isOutsideOpeningHours) {
                if (nextOpeningPeriod) {
                  orderIsOpen.value = true
                  storeIsOpen.value = true
                  updateUI(
                    textForOpen + `${nextOpeningPeriod.open}`,
                    classDefault,
                    storeData.name)
                } else if (nextDayOpening) {
                  orderIsOpen.value = false
                  storeIsOpen.value = false
                  updateUI(
                    `Le restaurant est fermé aujourd'hui. ${textNextOpen}`,
                    classClose,
                    storeData.name
                  )
                  return
                } else {
                  orderIsOpen.value = true
                  storeIsOpen.value = true
                  updateUI('', classOpen, storeData.name)
                }
              }
              break
          }
        }
      } else {
        orderIsOpen.value = false
        storeIsOpen.value = false
        updateUI(textOpenClose, classClose, storeData.name)
      }
    }
  }

  const updateUI = (message: string, className: string, storeName?: string) => {
    textTitle.value = message
    title.value = storeName ? (message ? `${storeName}: ${message}` : storeName) : message
    classTitle.value = className
  }

  return {
    updateStore,
    storeData,
    testDay,
    testHour,
    testMinute
  }
}